import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import { productTypesInversed } from '@teinor/erp/types/company/product'
import { ButtonToOrder, CustomRenderProductStockHistory, CustomRenderStockOperationType, CustomRenderStockReserveType } from '../../../../customRenderStock'
import { StockReservedHistoryModelType, StockReservedStatus } from '@teinor/erp/types/company/product/stockParent/stockReservedHistory'
import { CustomRenderDate, CustomRenderString } from '../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import { TwinSimpleTable } from '../../../../../../baseComponents/TwinTable'
import { ModalCreateEditStructureLayout } from '../../../../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import { TableProductStockTy, TableTypeStock } from '../../../types'
import { handleStockIsNegativeTextColor, isStockNegative } from '../../../functions'
import { StockOperationType } from '@teinor/erp/types/company/product/stockParent/stockHistory'
import { TwinDictionary } from '../../../../../../utils/globals/dictionary'
import { SwitchButton, TabItems } from '../../../../../../baseComponents/Button/SwitchButton'
import TwinTrans from '../../../../../../baseComponents/TwinTrans'
import { useState } from 'react'

interface TableProductStockReservedHistorysProps extends TableProductStockTy { }

const TableProductStockReservedHistorys: React.FC<TableProductStockReservedHistorysProps> = ({ productType, StockParentId, controlUnitType, tableType, unitMeasure, showSpecialCases }) => {
    const { t } = useTwinTranslation()
    const { tabIndex, setTabIndex } = useTableProductStockReservedHistorysLogic()
    const columns: ColumnTableSchema<StockReservedHistoryModelType> = [
        {
            id: 'date',
            dataKey: 'createdAt',
            label: t('date', 'Fecha'),
            customRender: (parameterValue) => <CustomRenderDate value={parameterValue} />,
            width: 110
        },
        {
            id: 'status',
            dataKey: 'status',
            label: t('status', 'Estado'),
            customRender: (parameterValue) => <CustomRenderStockReserveType value={parseInt(parameterValue) as keyof StockReservedStatus} />,
        },
        {
            id: 'operationFullAssignedNumber',
            dataKey: 'operationFullAssignedNumber',
            label: t('ref', 'Ref'),
            width: 85,
            customRender: (parameterValue, allRowData) => <CustomRenderString value={parameterValue || allRowData.operationId} />
        },
        {
            id: 'operationType',
            dataKey: 'operationType',
            label: t('operation', 'Operación'),
            customRender: (parameterValue) => <CustomRenderStockOperationType value={parameterValue as StockOperationType} />,
        },
    ]
    const onlyUnits = controlUnitType === 0
    const onlyMeasure = controlUnitType === 1
    const bothStocks = controlUnitType === 2

    if (onlyUnits || bothStocks) {
        columns.push({
            id: 'operationStock',
            dataKey: 'operationStock',
            label: t('reservedUnitsShort', 'Uds. reservadas'),
            minWidth: 120,
            customRender: (parameterValue) => <CustomRenderString value={parameterValue} />,
        })
        columns.push({
            id: 'currentStock',
            dataKey: 'currentStock',
            label: t('totalReservedUnitsShort', 'Total uds. reservadas'),
            minWidth: 155,
        })
    }
    if (onlyMeasure || bothStocks) {
        columns.push({
            id: 'operationMeasureStock',
            dataKey: 'operationMeasureStock',
            label: t('operationMeasureStock', 'Movimiento Medida'),
            customRender: (parameterValue) => <CustomRenderString className={handleStockIsNegativeTextColor(parameterValue)} value={isStockNegative(parameterValue) ? parameterValue + ' ' + unitMeasure : '+' + parameterValue + ' ' + unitMeasure} />,
        })
        columns.push({
            id: 'currentMeasureStock',
            dataKey: 'currentMeasureStock',
            label: t('totalReservedMeasure', 'Total medida reservada'),
            customRender: (parameterValue) => <CustomRenderString value={parameterValue + ' ' + unitMeasure} />,
        })
    }
    if (showSpecialCases) {
        if (tableType === 'individual') {
            columns.push({
                id: 'product',
                dataKey: 'MirrorStockChildId',
                label: t('product', 'Producto'),
                searchKey: 'name',
                customRender: (parameterValue, allRowData) => <CustomRenderProductStockHistory name={allRowData?.MirrorStockChild?.Product?.ProductLangs?.[0]?.name} unique_number={allRowData?.MirrorStockChild?.Product?.unique_number} id={String(allRowData?.MirrorStockChildId)} />,
                searchableComponent: {
                    component: 'SearchableComponentBasic',
                    extraComponentData: { label: t('product', 'Producto') }
                },
            })
        } else {
            columns.push({
                id: 'productGrouped',
                dataKey: 'StockParentId',
                label: t('product', 'Producto'),
                searchKey: productType === productTypesInversed['lots'] || productType === productTypesInversed['serialized'] ? 'unique_number' : 'name',
                customRender: (parameterValue, allRowData) =>  <CustomRenderProductStockHistory name={allRowData?.StockParent?.Product?.ProductLangs?.[0]?.name} unique_number={allRowData?.StockParent?.Product?.unique_number} id={String(allRowData?.id)} />,
                searchableComponent: {
                    component: 'SearchableComponentBasic',
                    extraComponentData: { label: t('product', 'Producto') }
                },
            })
        }
    }
    columns.push({
        id: 'operationId',
        dataKey: 'operationId',
        label: '',
        customRender: (parameterValue, allRowData) => <ButtonToOrder id={parameterValue} operationType={allRowData?.operationType} />,
        width: 50
    })

    const items: TabItems[] = [
        {
            element: <TwinTrans transKey={'all'}>Todos</TwinTrans>
        },
        {
            element: <TwinTrans transKey={'pendingP'}>Pendientes</TwinTrans>
        }
    ]
    const myAlreadyServedParams = tabIndex ? {alreadyServed: false} : {}
    const tableProps: Record<TableTypeStock, { getDataFrom: string, name: string, extraWhereParams: TwinDictionary }> = {
        'individual': { getDataFrom: '/api/app/product/stock/getLastStockReservedHistoriesOfStockParentId', name: 'productStockReserveHistories', extraWhereParams: { StockParentId, ...myAlreadyServedParams } },
        'groupByVariants': { getDataFrom: '/api/app/product/stock/getLastStockReservedHistoriesOfStockParentIdGroupByVariants', name: 'productStockReserveHistoriesGroupByVariants', extraWhereParams: { StockParentId, ...myAlreadyServedParams } },
        'groupByUniqueNumber': { getDataFrom: '/api/app/product/stock/getLastStockReservedHistoriesOfStockParentIdGroupByUniqueNumber', name: 'productStockReserveHistoriesGroupByUniqueNumber', extraWhereParams: { StockParentId, ...myAlreadyServedParams } },
    }
    return (
        <ModalCreateEditStructureLayout loading={false} translations={{
            title: <div className='flex items-center'>
                <h2>{t('lastReservedMovementStock', 'Últimos movimientos de reservas de stock')}</h2>
                <SwitchButton active={tabIndex} onClick={setTabIndex} items={items} className='w-fit ml-auto' />
            </div>,
            button: ''
        }} haveButtonPermissions={false} className='table_product_reserved_historys_stock flex-col' >
            <TwinSimpleTable columns={columns} {...tableProps[tableType]} />
        </ModalCreateEditStructureLayout>
    )
}

const useTableProductStockReservedHistorysLogic = () => {
    const [tabIndex, setTabIndex] = useState<number>(0)
    return { tabIndex, setTabIndex }
}



export default TableProductStockReservedHistorys